import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

export type SearchableSelectOption = {
  value: string;
  label: string;
  searchText?: string;
  disabled?: boolean;
};

function optionMatchesQuery(option: SearchableSelectOption, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = (option.searchText ?? option.label).toLowerCase();
  return q.split(/\s+/).filter(Boolean).every((token) => hay.includes(token));
}

export interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  emptyMessage?: string;
  id?: string;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  renderOption?: (option: SearchableSelectOption, selected: boolean) => React.ReactNode;
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Type to search…",
  disabled = false,
  emptyMessage = "No results found.",
  id,
  className,
  triggerClassName,
  contentClassName,
  renderOption,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value]
  );

  const filtered = useMemo(
    () => options.filter((o) => optionMatchesQuery(o, query)),
    [options, query]
  );

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "h-10 w-full justify-between border-gray-200 bg-white px-3 py-2 text-sm font-normal text-gray-900 hover:bg-white",
            !selected && "text-gray-500",
            triggerClassName,
            className
          )}
        >
          <span className="truncate text-left">
            {selected ? selected.label : value ? value : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "z-[200] w-[var(--radix-popover-trigger-width)] p-0",
          contentClassName
        )}
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          requestAnimationFrame(() => searchInputRef.current?.focus());
        }}
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <div className="border-b p-2" onPointerDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInputRef}
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Escape") {
                e.preventDefault();
                setOpen(false);
              }
            }}
          />
        </div>
        <div
          className="max-h-64 overflow-y-auto overscroll-contain p-1"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {filtered.length === 0 ? (
            <p className="text-muted-foreground p-3 text-sm">{emptyMessage}</p>
          ) : (
            filtered.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={option.disabled}
                  className={cn(
                    "hover:bg-muted flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm outline-none",
                    isSelected && "bg-muted/60",
                    option.disabled && "pointer-events-none opacity-50"
                  )}
                  onClick={() => {
                    onValueChange(option.value);
                    setOpen(false);
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Check
                    className={cn("h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")}
                  />
                  <span className="min-w-0 flex-1">
                    {renderOption ? renderOption(option, isSelected) : option.label}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
